'use client';

import { ArrowLeft } from 'lucide-react';

import { ConditionalWrapper } from '@/components/conditional-wrapper';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { tv } from '@/utils/styles';

export interface BreadcrumbItem {
  label: string | React.ReactNode;
  onClick?: () => void;
  href?: string;
  icon?: React.ReactNode;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  textClassName?: string;
  hideBackArrow?: boolean;
  variant?: 'white' | 'blue';
  noBottomBorder?: boolean;
  className?: string;
  actions?: React.ReactNode;
}

const Breadcrumbs = ({
  items,
  className,
  textClassName,
  hideBackArrow,
  noBottomBorder,
  variant,
  actions,
  ...props
}: BreadcrumbsProps) => {
  const { base, list, link, text, breadcrumbItem, iconWrapper, arrow } = styles({
    variant,
    hideBackArrow,
    noBottomBorder,
  });

  return (
    <nav className={base({ className })} {...props}>
      <ul className={list()}>
        {items.map((item, i) => (
          <li key={`breadcrumb-item-${i}`} className={breadcrumbItem()}>
            <ConditionalWrapper
              condition={!!item.onClick || !!item.href}
              // eslint-disable-next-line react/no-unstable-nested-components
              wrapper={(buttonChildren) => {
                if (item.onClick) {
                  return (
                    <button type="button" onClick={item.onClick} className={link()}>
                      {buttonChildren}
                    </button>
                  );
                }

                if (item.href) {
                  return (
                    <Link href={item.href} className={link()}>
                      {buttonChildren}
                    </Link>
                  );
                }

                return buttonChildren;
              }}
              key={`breadcrumb-item-${i}`}
            >
              <>
                {!hideBackArrow && i === 0 && <ArrowLeft className={arrow()} />}
                {item.icon && <span className={iconWrapper()}>{item.icon}</span>}
                <Text
                  variant="sm"
                  className={text({ class: textClassName })}
                  as="span"
                  dangerouslySetInnerHTML={{ __html: item.label ? item.label.toString() : '' }}
                />
              </>
            </ConditionalWrapper>
          </li>
        ))}
      </ul>
      {actions}
    </nav>
  );
};

const styles = tv({
  slots: {
    base: 'sticky top-0 z-6 flex items-center justify-between border-b border-b-grey-300 bg-grey-50',
    list: 'flex',
    link: 'decoration-none group flex h-full items-center [&_.breadcrumb-text]:text-grey-500 [&_svg]:hover:animate-bounce-x',
    breadcrumbItem:
      'relative flex h-10 items-center after:mx-2 after:inline-block after:text-baseSm after:text-grey-300 after:content-["/"] last:after:hidden lg:h-12 [&_.breadcrumb-text]:last:text-grey-800',
    text: 'breadcrumb-text relative top-px mb-0 border-b border-b-transparent text-baseSm font-normal leading-ultraTight text-grey-800 transition-[border-bottom] duration-200 ease-in-out first-letter:uppercase group-hover:border-grey-300',
    iconWrapper: 'mr-1 size-4 [&_svg]:size-4',
    arrow: 'mr-2 size-4',
  },
  variants: {
    variant: {
      white: {
        base: 'border-b border-grey-300 bg-white',
      },
      blue: {
        base: 'text-lightBlue-500',
        arrow: 'text-lightBlue-500',
        link: 'text-lightBlue-500 [&_.breadcrumb-text]:!text-lightBlue-500',
        text: 'text-lightBlue-500',
      },
    },
    hideBackArrow: {
      true: {
        breadcrumbItem: 'first:pl-4',
      },
      false: {},
    },
    noBottomBorder: {
      true: {
        base: 'border-none',
      },
    },
  },
});

export { Breadcrumbs };
